import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  BASIC_IMPORTS,
  MATERIAL_IMPORTS,
  PIPES_IMPORTS,
} from '../../shared/imports/common-imports';
import { RiskScoreDiagramComponent } from '../../shared/components/risk-score-diagram/risk-score-diagram.component';
import { BreadcrumbComponent } from '../../shared/components/breadcrumb/breadcrumb.component';
import ComplianceElements from '../../shared/data/compliance-elements';
import { THERAPEUTIC_AREAS } from '../../shared/data/therapeutic-areas';
import { FlagType } from '../../shared/models/top-vendors-data.model';
import { EmptyVendorFlagsPercentages } from '../../shared/models/percentages-response.model';
import { LambdaApiService } from '../../shared/services/lambda-api.service';
import { RiskScoreService } from '../../shared/services/risk-score.service';
import {
  ComplianceData,
  RiskData,
  RiskFlagData,
} from '../../shared/models/compliance-data.model';
import { ComplianceElementDetailComponent } from '../compliance-element-detail/compliance-element-detail.component';
import { VendorFlagsPercentages } from '../../shared/models/percentages-response.model';
import { DialogService } from '../../shared/services/dialog.service';
import { FilterVisualizerComponent } from '../../shared/components/filter-visualizer/filter-visualizer.component';

export interface CompareElement {
  code: string;
  data: ComplianceData | null;
  percentages: { flagType: FlagType; percentage: VendorFlagsPercentages }[];
  percentageSum: VendorFlagsPercentages;
  flagCount: number;
}

@Component({
  selector: 'app-compare',
  standalone: true,
  imports: [
    BASIC_IMPORTS,
    PIPES_IMPORTS,
    MATERIAL_IMPORTS,
    RiskScoreDiagramComponent,
    BreadcrumbComponent,
    ComplianceElementDetailComponent,
    FilterVisualizerComponent,
  ],
  templateUrl: './compare.component.html',
  styleUrls: [
    './compare.component.scss',
    '../ta-detail/ta-detail.component.scss',
    '../../shared/components/filter-visualizer/filter-visualizer.component.scss',
  ],
})
export class CompareComponent implements OnInit, OnDestroy {
  type: 'region' | 'market' | '' = '';
  isLoading = false;
  therapeuticArea = '';
  complianceElement = ComplianceElements[0];
  taMap = THERAPEUTIC_AREAS;
  title = '';
  elements: CompareElement[] = [];
  private paramSubscription: Subscription | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly apiService: LambdaApiService,
    private readonly riskScoreService: RiskScoreService,
    private readonly dialogService: DialogService,
  ) {}

  ngOnInit(): void {
    this.paramSubscription = this.route.paramMap.subscribe((params) => {
      this.type = (params.get('type') as 'region' | 'market') ?? '';
      const element1 = params.get('element1') ?? '';
      const element2 = params.get('element2') ?? '';

      // Initialize elements array
      this.elements = [element1, element2].map((code) => ({
        code,
        data: null,
        percentages: [
          {
            flagType: FlagType.OPERATIONAL,
            percentage: { ...EmptyVendorFlagsPercentages },
          },
          {
            flagType: FlagType.FINANCIAL,
            percentage: { ...EmptyVendorFlagsPercentages },
          },
        ],
        percentageSum: { ...EmptyVendorFlagsPercentages },
        flagCount: 0,
      }));

      this.isLoading = true;
      this.therapeuticArea = params.get('ta') ?? '';
      const elementName = params.get('element') ?? '';

      const complianceElement = ComplianceElements.find(
        (el) => el.link.toLowerCase() === elementName.toLowerCase(),
      );

      if (complianceElement) {
        this.complianceElement = complianceElement;
      }

      this.title = this.setTitle();

      this.loadData();
    });
  }

  setTitle(): string {
    const ta = this.taMap.find(
      (t) => t.id.toLowerCase() === this.therapeuticArea.toLowerCase(),
    );

    return ta ? ta.name : 'Overview';
  }

  loadData() {
    const promises: Promise<void>[] = [];

    // For each element, fetch data using the compare-specific methods
    this.elements.forEach((element) => {
      // Fetch percentages for this element
      element.percentages.forEach((p) => {
        const promise = this.apiService
          .getPercentagesForCompare(
            p.flagType,
            this.type as 'region' | 'market',
            element.code,
          )
          .then((data) => {
            p.percentage = {
              high: data.high,
              medium: data.medium,
              low: data.low,
            };
          });
        promises.push(promise);
      });

      // Fetch compliance data for this element
      const compliancePromise = this.apiService
        .fetchComplianceDataForCompare(
          this.type as 'region' | 'market',
          element.code,
        )
        .then((data: RiskData) => {
          element.flagCount = this.formatData(data, element.code);
        });
      promises.push(compliancePromise);

      // Fetch element data
      const dataPromise = this.apiService
        .getData(this.therapeuticArea, this.complianceElement.name)
        .then((data) => {
          element.data = { ...data };
          const ta = this.taMap.find(
            (t) => t.id.toLowerCase() === this.therapeuticArea.toLowerCase(),
          );
          if (ta && element.data) {
            element.data.therapeuticArea = { ...ta };
          }
        });
      promises.push(dataPromise);
    });

    Promise.all(promises).then(() => {
      // Calculate percentage sums and update "Assessing Risk" element for each
      this.elements.forEach((element) => {
        element.percentageSum = this.riskScoreService.sumPercentages(
          element.percentages.map((p) => p.percentage),
        );

        if (element.data) {
          const assesingRiskElement = element.data.elements.find(
            (e) => e.elementName === 'Assessing Risk',
          );

          if (assesingRiskElement) {
            assesingRiskElement.percentages = element.percentageSum;
            const colors = this.riskScoreService.calculatePercentageColor(
              element.percentageSum,
            );
            assesingRiskElement.color = colors.color;
            assesingRiskElement.colorName = colors.colorName;
          }
        }
      });

      this.isLoading = false;
    });
  }

  reloadData() {
    this.isLoading = true;
    this.loadData();
  }

  formatData(data: RiskData, element: string): number {
    if (!this.therapeuticArea || this.therapeuticArea !== 'BBU') {
      return 0;
    }

    const flags: RiskFlagData = {
      f1: 0,
      f2: 0,
      f3: 0,
      f4: 0,
      f5: 0,
      f6: 0,
    };

    data.regions.forEach((region) => {
      flags.f1 += region.totals.f1;
      flags.f2 += region.totals.f2;
      flags.f3 += region.totals.f3;
      flags.f4 += region.totals.f4;
      flags.f5 += region.totals.f5;
      flags.f6 += region.totals.f6;
    });

    return Object.values(flags).reduce((sum, val) => sum + val, 0);
  }

  ngOnDestroy(): void {
    this.paramSubscription?.unsubscribe();
  }

  clearCompare() {
    this.router.navigate([
      '/details',
      this.therapeuticArea,
      this.complianceElement.link,
    ]);
  }

  openCompareDialog() {
    this.dialogService.openCompareDialog(
      this.therapeuticArea,
      this.complianceElement.link,
    );
  }

  openDateRangePicker() {
    const dialogRef = this.dialogService.openDateRangePicker();

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.refresh) {
        this.reloadData();
      }
    });
  }

  clearDateRange() {
    this.reloadData();
  }
}
